import React, { useState, useEffect, useMemo } from 'react';
import { Box, Container, Typography, Divider, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button, Badge, Alert, Skeleton, CircularProgress, Snackbar, Select, MenuItem, FormControl, InputLabel, TextField, Tabs, Tab, Avatar, Chip, Card } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Search, Trash2, Banknote, Store, ShoppingCart, Package, Wallet, Percent, CheckCircle, AlertTriangle, TrendingUp, TrendingDown, ShoppingBag, Truck, RefreshCw, XCircle, Calendar } from 'lucide-react';
import CardMetric from '../components/CardMetric';
import useQontoBalance from '../hooks/useQontoBalance';
import useAmazonBalance from '../hooks/useAmazonBalance';
import useAchatsMetrics from '../hooks/useAchatsMetrics';
import useCommandesMetrics from '../hooks/useCommandesMetrics';
import api from '../utils/api';
import qontoService from '../services/qontoService';
import AchatsEnAttenteReception from '../components/AchatsEnAttenteReception';
import AchatsEnAttenteExpedition from '../components/AchatsEnAttenteExpedition';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, BarChart, Bar } from 'recharts';
import RepricingHistoryCard from '../components/RepricingHistoryCard';

// URLs des Seller Central par pays
const sellerCentralUrls = {
  FR: 'https://sellercentral.amazon.fr/orders-v3/order/',
  DE: 'https://sellercentral.amazon.de/orders-v3/order/',
  IT: 'https://sellercentral.amazon.it/orders-v3/order/',
  ES: 'https://sellercentral.amazon.es/orders-v3/order/',
  UK: 'https://sellercentral.amazon.co.uk/orders-v3/order/',
  NL: 'https://sellercentral.amazon.nl/orders-v3/order/',
  PL: 'https://sellercentral.amazon.pl/orders-v3/order/',
  SE: 'https://sellercentral.amazon.se/orders-v3/order/',
  BE: 'https://sellercentral.amazon.com.be/orders-v3/order/',
  IE: 'https://sellercentral.amazon.ie/orders-v3/order/'
};

// periodOptions pour le sélecteur de période
const periodOptions = [
  { value: 'today', label: "Aujourd'hui" },
  { value: 'yesterday', label: 'Hier' },
  { value: 'thisMonth', label: 'Ce mois' },
  { value: 'lastMonth', label: 'Mois dernier' },
  { value: 'thisYear', label: 'Cette année' },
  { value: 'lastYear', label: 'Année dernière' },
];

// countryFlags mapping (doit être défini avant usage)
const countryFlags = {
  FR: "https://flagcdn.com/24x18/fr.png",
  ES: "https://flagcdn.com/24x18/es.png",
  IT: "https://flagcdn.com/24x18/it.png",
  DE: "https://flagcdn.com/24x18/de.png",
  UK: "https://flagcdn.com/24x18/gb.png",
  NL: "https://flagcdn.com/24x18/nl.png",
  PL: "https://flagcdn.com/24x18/pl.png",
  SE: "https://flagcdn.com/24x18/se.png",
  BE: "https://flagcdn.com/24x18/be.png",
  IE: "https://flagcdn.com/24x18/ie.png"
};

// STATUS_COLORS mapping (doit être défini avant usage)
const STATUS_COLORS = {
  Shipped: 'success',
  Pending: 'warning',
  Canceled: 'error',
  Unshipped: 'default',
  PartiallyShipped: 'info',
  InvoiceUnconfirmed: 'default',
  Returned: 'info',
  Refund: 'error',
};

// formatAmount utilitaire (déjà défini plus haut, mais on le redéfinit pour la portée)
function formatAmount(amount) {
  return amount !== undefined && amount !== null && !isNaN(amount)
    ? Number(amount).toFixed(2) + " €"
    : "-";
}

// Utilitaires couleur pour les badges (reprend la logique page Commandes)
function getColorForValue(value, thresholds) {
  if (value === null || value === undefined) return { bg: '#f3f6fa', text: '#222' };
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return { bg: '#f3f6fa', text: '#222' };
  if (numValue >= thresholds.high) return { bg: '#e8f5e9', text: '#2e7d32' };
  if (numValue >= thresholds.medium) return { bg: '#fff3e0', text: '#ef6c00' };
  return { bg: '#ffebee', text: '#c62828' };
}

function BadgeMetricColored({ label, value, colorObj }) {
  const valueStr = String(value);
  const isVeryLong = valueStr.length > 10;
  return (
    <span style={{
      background: colorObj.bg,
      color: colorObj.text,
      borderRadius: 8,
      padding: '2px 10px',
      fontWeight: 600,
      marginRight: 4,
      display: 'inline-block',
      minWidth: 70,
      maxWidth: 120,
      textAlign: 'center',
      border: '1px solid #e0e0e0',
      fontFamily: 'inherit',
      verticalAlign: 'middle',
      whiteSpace: 'normal',
      lineHeight: 1.15,
    }}
      title={`${label} : ${value}`}
    >
      <span style={{ fontSize: '0.75em', fontWeight: 500, display: 'block' }}>{label}</span>
      <span style={{ fontSize: isVeryLong ? '0.85em' : '0.98em', fontWeight: 700, display: 'block', wordBreak: 'break-all' }}>{value}</span>
    </span>
  );
}

// Badges métriques pour la synthèse
function MetricBadge({ label, value, color = 'primary', isPercent, isCount }) {
  return (
    <Card sx={{ p: 1, minWidth: 90, textAlign: 'center', bgcolor: '#f8fafc', boxShadow: 'none', border: '1px solid #e0e0e0' }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="subtitle1" color={color} sx={{ fontWeight: 700 }}>
        {isCount
          ? parseInt(value, 10)
          : isPercent
            ? (Number(value).toFixed(1) + '%')
            : formatAmount(value)}
      </Typography>
    </Card>
  );
}

// Mini-métriques pour la card commandes
function MetricMini({ label, value, isPercent }) {
  return (
    <Chip
      label={isPercent ? (Number(value).toFixed(1) + '%') : formatAmount(value)}
      size="small"
      sx={{ fontWeight: 600, fontSize: '0.8em', bgcolor: '#e3eafc', color: '#1976d2', mr: 0.5 }}
    />
  );
}

const CardsSkeleton = () => (
    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Skeleton variant="rectangular" width={280} height={140} />
        <Skeleton variant="rectangular" width={280} height={140} />
        <Skeleton variant="rectangular" width={280} height={140} />
        <Skeleton variant="rectangular" width={280} height={140} />
    </Box>
);

const TableSkeleton = () => (
    <TableContainer component={Paper}>
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell>Nom du fichier</TableCell>
                    <TableCell>Date d'analyse</TableCell>
                    <TableCell>Lignes</TableCell>
                    <TableCell>Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {[1, 2, 3, 4, 5].map((i) => (
                    <TableRow key={i}>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton width={40} /></TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
);

function formatCurrency(amount) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(amount || 0);
}

// Fonction utilitaire pour convertir une date en heure de Paris
function toParisDate(dateStr) {
    // On s'assure que la date est en UTC
    let utcDate;
    if (dateStr.endsWith('Z')) {
        utcDate = dateStr;
    } else if (dateStr.includes('+')) {
        utcDate = dateStr;
    } else {
        // Si la date n'a pas de suffixe, on considère qu'elle est en UTC
        utcDate = dateStr + 'Z';
    }
    
    // On crée la date en UTC
    const d = new Date(utcDate);
    
    // On utilise toLocaleString avec timeZone pour obtenir la date en heure de Paris
    const parisStr = d.toLocaleString('fr-FR', { 
        timeZone: 'Europe/Paris',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    });
    
    // On parse la chaîne pour créer une nouvelle date
    const [datePart, timePart] = parisStr.split(' ');
    const [day, month, year] = datePart.split('/');
    const [hour, minute, second] = (timePart || '00:00:00').split(':');
    
    // On crée une nouvelle date en heure de Paris
    return new Date(
        Number(year), 
        Number(month) - 1, 
        Number(day),
        Number(hour), 
        Number(minute), 
        Number(second || 0)
    );
}

// Fonction pour créer une date en heure de Paris
function createParisDate(year, month, day, hour = 0, minute = 0, second = 0) {
    // On crée d'abord une date en UTC
    const utcDate = new Date(Date.UTC(year, month, day, hour, minute, second));
    // On la convertit en heure de Paris
    return toParisDate(utcDate.toISOString());
}

// Fonction utilitaire pour obtenir minuit et 23:59:59 heure de Paris en UTC pour un jour donné
function getParisDayBoundsUTC(date = new Date()) {
  // On veut minuit et 23:59:59 heure de Paris pour la date donnée, en UTC
  // 1. On récupère l'année, le mois, le jour de la date en heure de Paris
  const paris = new Date(date.toLocaleString('en-US', { timeZone: 'Europe/Paris' }));
  const year = paris.getFullYear();
  const month = paris.getMonth();
  const day = paris.getDate();

  // 2. On crée les dates minuit et 23:59:59 heure de Paris
  // On crée une date ISO explicite en heure de Paris
  // On détermine l'offset de Paris pour ce jour-là
  const minuitParis = new Date(Date.UTC(year, month, day, 0, 0, 0));
  const finParis = new Date(Date.UTC(year, month, day, 23, 59, 59, 999));

  // On récupère l'offset de Paris pour ce jour-là
  const offsetMinuit = -new Date(minuitParis.toLocaleString('en-US', { timeZone: 'Europe/Paris' })).getTimezoneOffset();
  const offsetFin = -new Date(finParis.toLocaleString('en-US', { timeZone: 'Europe/Paris' })).getTimezoneOffset();

  // On applique l'offset pour obtenir l'heure UTC correspondant à minuit et fin de journée Paris
  const start = new Date(minuitParis.getTime() - offsetMinuit * 60000);
  const end = new Date(finParis.getTime() - offsetFin * 60000);

  return { start, end };
}

// BadgeMetric pour les métriques commandes
function BadgeMetricOrder({ label, value }) {
  return (
    <span style={{
      background: '#f3f6fa',
      color: '#1976d2',
      borderRadius: 8,
      padding: '2px 10px',
      fontWeight: 600,
      fontSize: '0.95em',
      marginRight: 4,
      display: 'inline-block',
      whiteSpace: 'nowrap',
      minWidth: 70,
      textAlign: 'center',
      border: '1px solid #e0e0e0',
    }}>
      {label} : {value}
    </span>
  );
}

// Nouveau composant badge compact
function BadgeMetricCompact({ label, value, tooltip }) {
  // Calcul dynamique de la taille de police selon la longueur du texte
  const valueStr = String(value);
  let fontSize = '1em';
  if (valueStr.length > 8) fontSize = '0.85em';
  if (valueStr.length > 12) fontSize = '0.75em';

  const content = (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      minWidth: 56,
      maxWidth: 90,
      px: 1,
      py: 0.5,
      bgcolor: '#f3f6fa',
      borderRadius: 1,
      border: '1px solid #e0e0e0',
      fontWeight: 600,
      fontSize,
      textAlign: 'center',
      lineHeight: 1.1,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }}>
      <span style={{ fontSize: '0.72em', color: '#888', fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize, color: '#1976d2', fontWeight: 700 }}>{value}</span>
    </Box>
  );
  return tooltip ? (
    <Tooltip title={tooltip} arrow>{content}</Tooltip>
  ) : content;
}

// Ajout utilitaire pour calculer le nombre de refunds et la perte associée
function getRefundMetrics(commandes) {
  const refunds = commandes.filter(cmd => cmd.Statut === 'Refund');
  const refundCount = refunds.length;
  const refundLoss = refunds.reduce((sum, cmd) => sum + (Number(cmd.RefundLossHT) || 0), 0);
  return { refundCount, refundLoss };
}

const Dashboard = () => {
    const [analysedFiles, setAnalysedFiles] = React.useState([]);
    const [hasNewFiles, setHasNewFiles] = React.useState(false);
    const [achats, setAchats] = React.useState([]);
    const [produits, setProduits] = React.useState([]);
    const [fournisseurs, setFournisseurs] = React.useState([]);
    const [statuts, setStatuts] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);
    const [fileToDelete, setFileToDelete] = React.useState(null);
    const [openQontoDialog, setOpenQontoDialog] = useState(false);
    const [openAmazonDialog, setOpenAmazonDialog] = useState(false);
    const [openAchatsDialog, setOpenAchatsDialog] = useState(false);
    const [openStockDialog, setOpenStockDialog] = useState(false);
    const [qontoTransactions, setQontoTransactions] = useState([]);
    const [loadingQontoTransactions, setLoadingQontoTransactions] = useState(false);
    const [errorQontoTransactions, setErrorQontoTransactions] = useState(null);
    const [isQontoDialogOpen, setIsQontoDialogOpen] = useState(false);
    const [tvaCredit, setTvaCredit] = useState(() => {
      const saved = localStorage.getItem('card-tva');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          return parsed.value ? Number(parsed.value) : 0;
        } catch {}
      }
      return 0;
    });
    const [thresholds, setThresholds] = useState(() => {
      const saved = localStorage.getItem("commandesThresholds");
      return saved ? JSON.parse(saved) : {
        roi: { high: 50, medium: 30, enabled: true },
        profit: { high: 100, medium: 50, enabled: true },
        marge: { high: 30, medium: 15, enabled: true },
      };
    });
    const [loadingAchatsReception, setLoadingAchatsReception] = useState(false);
    const [notification, setNotification] = useState({
      open: false,
      message: '',
      severity: 'success'
    });
    const navigate = useNavigate();
    const [achatsEnAttenteReception, setAchatsEnAttenteReception] = useState([]);
    const [commandes, setCommandes] = useState([]);
    const [loadingCommandes, setLoadingCommandes] = useState(false);
    const [selectedPeriod, setSelectedPeriod] = useState('today');
    const [customDateRange, setCustomDateRange] = useState({
        start: null,
        end: null
    });
    const [openDatePicker, setOpenDatePicker] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [openProductDialog, setOpenProductDialog] = useState(false);
    const [depensesMois, setDepensesMois] = useState([]);
    const [totalDepensesMois, setTotalDepensesMois] = useState(0);
    const [amazonDepensesMois, setAmazonDepensesMois] = useState([]);
    const [totalAmazonDepensesMois, setTotalAmazonDepensesMois] = useState(0);
    const [achatsUpdateTrigger, setAchatsUpdateTrigger] = useState(0);

    const totalTvaDepensesMois = useMemo(() => {
        if (!depensesMois) return 0;
        return depensesMois.reduce((sum, d) => sum + Number(d.montant_tva || 0), 0);
    }, [depensesMois]);

    // Fonction pour obtenir un achat par son ID
    const getAchat = (id) => {
        if (!achats || !Array.isArray(achats)) return null;
        return achats.find(a => a.id === id);
    };

    // Hooks métiers
    const { balance: qontoBalance, loading: loadingQonto, error: errorQonto } = useQontoBalance();
    const { balance: amazonBalance, loading: loadingAmazon, error: errorAmazon } = useAmazonBalance();
    const { coutTotalAchatsEnCours, stockAmazon, achatsEnCours, achatsStockAmazon } = useAchatsMetrics(achats, produits, statuts);
    const commandesMetrics = useCommandesMetrics(commandes);

    useEffect(() => {
      const onStorage = () => {
        const saved = localStorage.getItem('card-tva');
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            setTvaCredit(parsed.value ? Number(parsed.value) : 0);
          } catch {}
        }
      };
      window.addEventListener('storage', onStorage);
      return () => window.removeEventListener('storage', onStorage);
    }, []);

    React.useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [achatsRes, produitsRes, statutsRes, fournisseursRes, commandesRes] = await Promise.all([
                api.get("/api/achats"),
                api.get("/api/produits"),
                api.get("/api/statut"),
                api.get("/api/fournisseurs"),
                api.get("/api/commandes")
            ]);
            setAchats(achatsRes.data);
            setProduits(produitsRes.data);
            setStatuts(statutsRes.data);
            setFournisseurs(fournisseursRes.data);
            setCommandes(commandesRes.data);
            await fetchAnalysedFiles();
        } catch (err) {
            setError("Erreur lors du chargement des données");
        } finally {
            setLoading(false);
        }
    };

    const fetchAnalysedFiles = async () => {
        try {
            const filesRes = await api.get('/api/analysed-files');
            const data = filesRes.data.data;
            const sortedData = data.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
            const formattedData = sortedData.map(file => ({
                id: file.name,
                name: file.name,
                analysisDate: new Date(file.updated_at).toLocaleString('fr-FR'),
                lignes: file.lignes,
                isNew: file.isNew
            }));
            setAnalysedFiles(formattedData);
            const newFiles = formattedData.some(file => file.isNew);
            setHasNewFiles(newFiles);
            if (newFiles && !localStorage.getItem('notificationShown')) {
                showNotification();
            }
        } catch (err) {
            //
        }
    };

    const showNotification = () => {
        if ('Notification' in window) {
            Notification.requestPermission().then(permission => {
                if (permission === 'granted') {
                    new Notification('Nouveaux fichiers analysés', {
                        body: 'De nouveaux fichiers sont disponibles pour analyse',
                        icon: '/logo192.png',
                        data: { url: '/dashboard' }
                    });
                    localStorage.setItem('notificationShown', 'true');
                }
            });
        }
    };

    const handleFileClick = (file) => {
        navigate('/analyse', { state: { sourceFile: file.name } });
    };

    const handleDeleteFile = async (file) => {
        try {
            // Suppression locale
            const response = await api.delete(`/api/analysed-files/${encodeURIComponent(file.name)}`);
            
            if (response.data.success) {
                // Mise à jour de l'état local
                setAnalysedFiles(prevFiles => prevFiles.filter(f => f.name !== file.name));
                
                // Suppression sur le VPS uniquement en production
                if (process.env.NODE_ENV === 'production') {
                    try {
                        await api.delete('/analyzed-file', {
                            data: { filename: file.name }
                        });
                    } catch (vpsError) {
                        console.warn("La suppression sur le VPS a échoué, mais le fichier a été supprimé localement:", vpsError);
                    }
                }
            } else {
                throw new Error('Erreur lors de la suppression du fichier');
            }
        } catch (error) {
            console.error("Erreur lors de la suppression:", error);
            // Afficher une notification d'erreur à l'utilisateur
            alert("Erreur lors de la suppression du fichier. Veuillez réessayer.");
        } finally {
            setOpenDeleteDialog(false);
            setFileToDelete(null);
        }
    };

    const handleDeleteClick = (file) => {
        setFileToDelete(file);
        setOpenDeleteDialog(true);
    };

    // Fonction pour obtenir le nom du produit à partir de son ID
    const getProduitName = (produitId) => {
      if (!produits || !Array.isArray(produits)) return "Produit inconnu";
      const produit = produits.find(p => p.id === produitId);
      return produit ? produit.Titre : "Produit inconnu";
    };

    // Détail Qonto (transactions)
    const handleOpenQontoDialog = async () => {
      setLoadingQontoTransactions(true);
      setErrorQontoTransactions(null);
      try {
        const data = await qontoService.getTransactions();
        setQontoTransactions(data);
      } catch (err) {
        setErrorQontoTransactions("Impossible de récupérer les transactions Qonto");
      } finally {
        setLoadingQontoTransactions(false);
        setIsQontoDialogOpen(true);
      }
    };

    const qontoDialogContent = (
      loadingQontoTransactions ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
      ) : errorQontoTransactions ? (
        <Alert severity="error">{errorQontoTransactions}</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Montant</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {qontoTransactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">Aucune transaction disponible</TableCell>
                </TableRow>
              ) : (
                qontoTransactions.map((transaction, idx) => (
                  <TableRow key={transaction.id || idx}>
                    <TableCell>{transaction.date ? new Date(transaction.date).toLocaleDateString('fr-FR') : ''}</TableCell>
                    <TableCell>{transaction.label}</TableCell>
                    <TableCell align="right" style={{ color: transaction.side === 'credit' ? '#28A745' : '#E3342F', fontWeight: 'bold' }}>
                      {transaction.side === 'credit' ? '+' : ''}{formatCurrency(transaction.amount)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )
    );

    // Détail Amazon (par pays)
    const amazonDetails = amazonBalance?.details || [];
    const amazonDialogContent = (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Pays</TableCell>
              <TableCell align="right">Montant</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {amazonDetails.map((detail, idx) => (
              <TableRow key={detail.country || idx}>
                <TableCell>{detail.country}</TableCell>
                <TableCell align="right">{formatCurrency(detail.amount)}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell><strong>Total</strong></TableCell>
              <TableCell align="right"><strong>{formatCurrency(amazonBalance?.amount)}</strong></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );

    // Détail Achats en cours
    const achatsDialogContent = (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Produit</TableCell>
              <TableCell>Quantité</TableCell>
              <TableCell align="right">Montant</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {achatsEnCours && achatsEnCours.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">Aucun achat en cours</TableCell>
              </TableRow>
            ) : (
              achatsEnCours.map((item, idx) => (
                <TableRow key={item.id || idx}>
                  <TableCell>{getProduitName(item.Produit)}</TableCell>
                  <TableCell>{item.Quantité}</TableCell>
                  <TableCell align="right">{formatCurrency(Number(item.prixAchat) * Number(item.Quantité))}</TableCell>
                </TableRow>
              ))
            )}
            <TableRow>
              <TableCell><strong>Total</strong></TableCell>
              <TableCell></TableCell>
              <TableCell align="right"><strong>{formatCurrency(coutTotalAchatsEnCours)}</strong></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );

    // Détail Stock Amazon
    const stockDialogContent = (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Produit</TableCell>
              <TableCell>Stock</TableCell>
              <TableCell align="right">Montant</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {achatsStockAmazon && achatsStockAmazon.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">Aucun stock Amazon</TableCell>
              </TableRow>
            ) : (
              achatsStockAmazon.map((item, idx) => (
                <TableRow key={item.id || idx}>
                  <TableCell>{getProduitName(item.Produit)}</TableCell>
                  <TableCell>{item.stockRestant}</TableCell>
                  <TableCell align="right">{formatCurrency(Number(item.prixAchat) * Number(item.stockRestant))}</TableCell>
                </TableRow>
              ))
            )}
            <TableRow>
              <TableCell><strong>Total</strong></TableCell>
              <TableCell></TableCell>
              <TableCell align="right"><strong>{formatCurrency(stockAmazon)}</strong></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );

    // Fonction pour obtenir les achats en attente de réception
    const getAchatsEnAttenteReception = () => {
      return achats.filter(achat => {
        const statutLabel = getStatutLabel(achat.Statut);
        return statutLabel === "En attente réception";
      });
    };

    // Fonction pour mettre à jour le statut d'un achat
    const handleUpdateStatut = async (achatId) => {
      try {
        setLoadingAchatsReception(true);
        // Trouver le statut "En attente d'expédition"
        const statutExpedition = statuts.find(s => s.Titre === "En attente d'expédition");
        if (!statutExpedition) {
          throw new Error("Statut 'En attente d'expédition' non trouvé");
        }
        // Mettre à jour l'achat
        await api.put(`/api/achats/${achatId}`, {
          Statut: statutExpedition.id
        });
        // Rafraîchir les données
        await fetchData();
        setAchatsUpdateTrigger(t => t + 1); // Incrémente le trigger
        setNotification({
          open: true,
          message: "Statut mis à jour avec succès",
          severity: "success"
        });
      } catch (error) {
        console.error("Erreur lors de la mise à jour du statut:", error);
        setNotification({
          open: true,
          message: "Erreur lors de la mise à jour du statut",
          severity: "error"
        });
      } finally {
        setLoadingAchatsReception(false);
      }
    };

    // Fonctions utilitaires pour le formatage
    const getStatutLabel = (id) => {
        if (!statuts || !Array.isArray(statuts)) return id;
        // Vérifier si l'ID est directement un objet statut
        if (typeof id === 'object' && id !== null) {
            return id.Titre || id;
        }
        // Chercher dans le tableau des statuts
        const statut = statuts.find((s) => {
            if (typeof s === 'object' && s !== null) {
                return s.id === id;
            }
            return false;
        });
        return statut ? statut.Titre : id;
    };

    const getProduitLabel = (id) => {
        if (!produits || !Array.isArray(produits)) return id;
        const produit = produits.find((p) => p.id === id);
        return produit ? produit.Titre : id;
    };

    const getFournisseurLabel = (id) => {
        if (!fournisseurs || !Array.isArray(fournisseurs)) return id;
        const fournisseur = fournisseurs.find((f) => f.id === id);
        return fournisseur ? fournisseur.Titre : id;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        return d.toLocaleDateString('fr-FR');
    };

    useEffect(() => {
        const fetchAchatsEnAttenteReception = async () => {
            try {
                const res = await api.get('/api/achats/en-attente');
                setAchatsEnAttenteReception(Array.isArray(res.data) ? res.data : []);
            } catch (e) {
                setAchatsEnAttenteReception([]);
            }
        };
        fetchAchatsEnAttenteReception();
    }, [/* tu peux ajouter fetchData si besoin de refresh */]);

    // Fonction pour calculer les dates en fonction de la période sélectionnée
    const getDateRange = () => {
        switch (selectedPeriod) {
            case 'today': {
                const { start, end } = getParisDayBoundsUTC();
                return { start, end };
            }
            case 'yesterday': {
                const d = new Date();
                d.setDate(d.getDate() - 1);
                const { start, end } = getParisDayBoundsUTC(d);
                return { start, end };
            }
            case 'thisMonth': {
                // Premier jour du mois en Paris
                const now = new Date();
                const parisNow = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Paris' }));
                const year = parisNow.getFullYear();
                const month = parisNow.getMonth();
                const startParis = new Date(Date.UTC(year, month, 1));
                const { start } = getParisDayBoundsUTC(startParis);
                // Dernier jour du mois
                const lastDayParis = new Date(Date.UTC(year, month + 1, 0));
                const { end } = getParisDayBoundsUTC(lastDayParis);
                return { start, end };
            }
            case 'lastMonth': {
                const now = new Date();
                const parisNow = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Paris' }));
                let year = parisNow.getFullYear();
                let month = parisNow.getMonth() - 1;
                if (month < 0) {
                    month = 11;
                    year -= 1;
                }
                const startParis = new Date(Date.UTC(year, month, 1));
                const { start } = getParisDayBoundsUTC(startParis);
                const lastDayParis = new Date(Date.UTC(year, month + 1, 0));
                const { end } = getParisDayBoundsUTC(lastDayParis);
                return { start, end };
            }
            case 'thisYear': {
                const now = new Date();
                const parisNow = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Paris' }));
                const year = parisNow.getFullYear();
                const startParis = new Date(Date.UTC(year, 0, 1));
                const { start } = getParisDayBoundsUTC(startParis);
                const lastDayParis = new Date(Date.UTC(year, 11, 31));
                const { end } = getParisDayBoundsUTC(lastDayParis);
                return { start, end };
            }
            case 'lastYear': {
                const now = new Date();
                const parisNow = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Paris' }));
                const year = parisNow.getFullYear() - 1;
                const startParis = new Date(Date.UTC(year, 0, 1));
                const { start } = getParisDayBoundsUTC(startParis);
                const lastDayParis = new Date(Date.UTC(year, 11, 31));
                const { end } = getParisDayBoundsUTC(lastDayParis);
                return { start, end };
            }
            case 'custom':
                return {
                    start: customDateRange.start ? toParisDate(customDateRange.start) : null,
                    end: customDateRange.end ? toParisDate(customDateRange.end) : null
                };
            default: {
                const { start, end } = getParisDayBoundsUTC();
                return { start, end };
            }
        }
    };

    // Filtrer les commandes pour exclure les annulées
    const commandesFiltrees = useMemo(() => commandes.filter(cmd => cmd.Statut !== 'Canceled'), [commandes]);

    // Fonction pour filtrer les commandes selon la période
    const getFilteredCommandes = () => {
        const { start, end } = getDateRange();
        return commandesFiltrees.filter(commande => {
            // On prend la date brute (en UTC)
            const commandeDate = new Date(commande.DateCommande);
            // On compare en UTC
            return commandeDate >= start && commandeDate <= end;
        });
    };

    // Calcul des métriques pour la période sélectionnée
    const periodMetrics = useMemo(() => {
        const filteredCommandes = getFilteredCommandes();
        const totalVentes = filteredCommandes.reduce((sum, cmd) => sum + (Number(cmd.PrixDeVente) || 0), 0);
        const totalProfit = filteredCommandes.reduce((sum, cmd) => sum + (Number(cmd.Profit) || 0), 0);
        const totalROI = filteredCommandes.reduce((sum, cmd) => sum + (Number(cmd.ROI) || 0), 0);
        const totalMarge = filteredCommandes.reduce((sum, cmd) => sum + (Number(cmd.Marge) || 0), 0);
        const count = filteredCommandes.length;
        const { refundCount, refundLoss } = getRefundMetrics(filteredCommandes);
        return {
            ventes: totalVentes,
            profit: totalProfit,
            moyenneROI: count > 0 ? totalROI / count : 0,
            moyenneMarge: count > 0 ? totalMarge / count : 0,
            commandes: count,
            refundCount,
            refundLoss
        };
    }, [commandes, selectedPeriod, customDateRange]);

    const handleCustomDateChange = (field, value) => {
        setCustomDateRange(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleCustomDateSubmit = () => {
        if (customDateRange.start && customDateRange.end) {
            setSelectedPeriod('custom');
            setOpenDatePicker(false);
        }
    };

    // Ajout des fonctions manquantes
    const getProduit = (id) => {
        if (!produits || !Array.isArray(produits)) return null;
        return produits.find(p => p.id === id);
    };

    const handleOpenProductDetail = (productId) => {
        const product = getProduit(productId);
        if (product) {
            setSelectedProduct(product);
            setOpenProductDialog(true);
        }
    };

    // Calcul des métriques mensuelles
    const monthlyMetrics = useMemo(() => {
        if (!commandes) return { ventes: 0, profit: 0, commandes: 0, refundCount: 0, refundLoss: 0 };
        
        const today = new Date();
        const startOfMonth = createParisDate(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = createParisDate(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59);

        const monthlyCommandes = commandes.filter(commande => {
            const commandeDate = toParisDate(commande.DateCommande);
            return commandeDate >= startOfMonth && commandeDate <= endOfMonth;
        });
        const { refundCount, refundLoss } = getRefundMetrics(monthlyCommandes);
        return {
            ventes: monthlyCommandes.reduce((sum, cmd) => sum + (Number(cmd.PrixDeVente) || 0), 0),
            profit: monthlyCommandes.reduce((sum, cmd) => sum + (Number(cmd.Profit) || 0), 0),
            commandes: monthlyCommandes.length,
            refundCount,
            refundLoss
        };
    }, [commandes]);

    // Calcul des produits les plus rentables du mois
    const topProducts = useMemo(() => {
        if (!commandes || !produits) return [];
        
        const today = new Date();
        const startOfMonth = createParisDate(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = createParisDate(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59);

        // Filtrer les commandes du mois
        const monthlyCommandes = commandes.filter(commande => {
            const commandeDate = toParisDate(commande.DateCommande);
            return commandeDate >= startOfMonth && commandeDate <= endOfMonth;
        });
        
        // Calculer les métriques par produit
        const productMetrics = monthlyCommandes.reduce((acc, commande) => {
            const produit = getProduit(commande.Produit);
            if (!produit) return acc;

            if (!acc[produit.id]) {
                acc[produit.id] = {
                    id: produit.id,
                    titre: produit.Titre,
                    ventes: 0,
                    profit: 0,
                    roi: 0,
                    marge: 0,
                    count: 0
                };
            }

            acc[produit.id].ventes += Number(commande.PrixDeVente) || 0;
            acc[produit.id].profit += Number(commande.Profit) || 0;
            acc[produit.id].roi += Number(commande.ROI) || 0;
            acc[produit.id].marge += Number(commande.Marge) || 0;
            acc[produit.id].count += 1;

            return acc;
        }, {});

        // Convertir en tableau et calculer les moyennes
        return Object.values(productMetrics)
            .map(metric => ({
                ...metric,
                roi: metric.count > 0 ? metric.roi / metric.count : 0,
                marge: metric.count > 0 ? metric.marge / metric.count : 0
            }))
            .sort((a, b) => b.profit - a.profit)
            .slice(0, 5);
    }, [commandes, produits]);

    // Calcul du panier moyen
    const panierMoyen = periodMetrics.commandes > 0 ? periodMetrics.ventes / periodMetrics.commandes : 0;

    // Préparation des données pour le graphique (exemple : CA par jour sur la période)
    const chartData = useMemo(() => {
      // On regroupe par jour (ou heure si today)
      const data = {};
      getFilteredCommandes().forEach(cmd => {
        const d = toParisDate(cmd.DateCommande);
        let key;
        if (selectedPeriod === 'today' || selectedPeriod === 'yesterday') {
          key = d.getHours().toString().padStart(2, '0') + 'h';
        } else {
          key = d.toLocaleDateString('fr-FR');
        }
        if (!data[key]) data[key] = { label: key, ventes: 0, profit: 0, commandes: 0 };
        data[key].ventes += Number(cmd.PrixDeVente) || 0;
        data[key].profit += Number(cmd.Profit) || 0;
        data[key].commandes += 1;
      });
      return Object.values(data).sort((a, b) => a.label.localeCompare(b.label, 'fr'));
    }, [commandes, selectedPeriod, customDateRange]);

    // Top produits de la période filtrée
    const topProductsPeriod = useMemo(() => {
      const filtered = getFilteredCommandes();
      const map = {};
      filtered.forEach(cmd => {
        if (!cmd.Produit) return;
        if (!map[cmd.Produit]) map[cmd.Produit] = { id: cmd.Produit, ventes: 0, profit: 0, titre: getProduitLabel(cmd.Produit), count: 0 };
        map[cmd.Produit].ventes += Number(cmd.PrixDeVente) || 0;
        map[cmd.Produit].profit += Number(cmd.Profit) || 0;
        map[cmd.Produit].count += 1;
      });
      return Object.values(map).sort((a, b) => b.profit - a.profit).slice(0, 3);
    }, [commandes, selectedPeriod, customDateRange]);

    const renderCard = (row) => {
      const produit = getProduit(row.Produit);
      const achat = getAchat(row.Achat);
      const asin = produit?.ASIN;
      const imgUrl = asin ? `https://images-na.ssl-images-amazon.com/images/P/${asin}.01._SCLZZZZZZZ_.jpg` : null;
      const titre = produit?.Titre || "Produit";
      const statut = row.Statut || '-';
      const orderLink = sellerCentralUrls[row.Pays] ? sellerCentralUrls[row.Pays] + row.AmazonOrderId : null;
      const flagUrl = countryFlags[row.Pays];
      const flagUrlLivraison = countryFlags[row.PaysLivraison];
      const prixAchat = achat?.prixAchat || achat?.["Prix d'achat"] || 0;
      const txTvaAchat = achat?.TxTVA || null;
      const txTvaVente = row.TxTVA || null;
      const tvaCollectee = (row.PrixDeVente && txTvaVente) ? ((Number(row.PrixDeVente) / (1 + Number(txTvaVente)/100)) * (Number(txTvaVente)/100)) : null;
      const tvaDeductible = (prixAchat > 0 && txTvaAchat) ? ((Number(prixAchat) / (1 + Number(txTvaAchat)/100)) * (Number(txTvaAchat)/100)) : null;
      const fraisPrep = achat?.frais_preparation || 0;
      const fraisLiv = achat?.frais_livraison || 0;
      const totalFees = (Number(row.fraisFBA) || 0) + (Number(row.fraisReferral) || 0) + (Number(row.feeDigitalServices) || 0) + (Number(row.feeVariable) || 0);
      const profitColors = getColorForValue(row.Profit, thresholds.profit);
      const roiColors = getColorForValue(row.ROI, thresholds.roi);
      const margeColors = getColorForValue(row.Marge, thresholds.marge);
      
      // Créer les listes de frais non nuls
      const amazonFees = [];
      if (Number(row.fraisFBA) > 0) amazonFees.push(`FBA : ${formatAmount(row.fraisFBA)}`);
      if (Number(row.fraisReferral) > 0) amazonFees.push(`Referral : ${formatAmount(row.fraisReferral)}`);
      if (Number(row.feeDigitalServices) > 0) amazonFees.push(`Services numériques : ${formatAmount(row.feeDigitalServices)}`);
      if (Number(row.feeVariable) > 0) amazonFees.push(`Variable closing : ${formatAmount(row.feeVariable)}`);

      const fraisEtTvaTooltip = (
        <Box>
          {amazonFees.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#000', fontWeight: 700 }}>Frais Amazon</Typography>
              <Box sx={{ mb: 2 }}>
                {amazonFees.map((fee, index) => (
                  <div key={index}>{fee}</div>
                ))}
              </Box>
            </>
          )}
          <Box>
            <div>TVA collectée : {tvaCollectee !== null ? formatAmount(tvaCollectee) : '-'} {txTvaVente ? `- TVA : ${txTvaVente}%` : ''}</div>
            <div>TVA déductible : {tvaDeductible !== null ? formatAmount(tvaDeductible) : '-'} {txTvaAchat ? `- TVA : ${txTvaAchat}%` : ''}</div>
          </Box>
        </Box>
      );

      // Tooltip pour le coût total
      const coutTotalTooltip = (
        <Box>
          <div>Prix d'achat : {prixAchat > 0 ? formatAmount(prixAchat) : '-'}</div>
          {fraisPrep > 0 && <div>Frais de préparation : {formatAmount(fraisPrep)}</div>}
          {fraisLiv > 0 && <div>Frais de livraison : {formatAmount(fraisLiv)}</div>}
        </Box>
      );

      return (
        <TableRow
          key={row.id}
          hover
          onClick={() => handleFileClick(row)}
          sx={{ cursor: 'pointer' }}
        >
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {imgUrl ? (
                <img src={imgUrl} alt={titre} style={{ width: 48, height: 48, objectFit: 'contain' }} />
              ) : (
                <Box sx={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: 22 }}>?</Box>
              )}
              <Typography variant="body2" noWrap title={titre}>{titre}</Typography>
            </Box>
          </TableCell>
          <TableCell>{formatDate(row.DateCommande)}</TableCell>
          <TableCell>{row.PrixDeVente ? formatCurrency(row.PrixDeVente) : '-'}</TableCell>
          <TableCell>
            <Tooltip title={fraisEtTvaTooltip}>
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  handleFileClick(row);
                }}
                color="primary"
                size="small"
                sx={{ mr: 1 }}
              >
                <Search size={20} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Supprimer">
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  handleDeleteClick(row);
                }}
                color="error"
                size="small"
              >
                <Trash2 size={20} />
              </IconButton>
            </Tooltip>
          </TableCell>
        </TableRow>
      );
    };

    const fetchDepensesMois = async () => {
      try {
        const res = await api.get('/api/depenses');
        const now = new Date();
        const mois = now.getMonth();
        const annee = now.getFullYear();
        let depenses = Array.isArray(res.data) ? res.data : [];
        let depensesDuMois = [];
        // Dépenses fixes du mois
        depensesDuMois = depensesDuMois.concat(
          depenses.filter(d => {
            if (d.est_recurrent) return false;
            const date = new Date(d.date_debut);
            return date.getMonth() === mois && date.getFullYear() === annee;
          })
        );
        // Occurrences récurrentes du mois
        depenses.forEach(d => {
          if (d.est_recurrent) {
            const dateDebut = new Date(d.date_debut);
            const dateFin = d.date_fin ? new Date(d.date_fin) : null;
            let current = new Date(dateDebut);

            // On avance jusqu'à atteindre le mois/année courants ou dépasser
            while (
              (current.getFullYear() < annee) ||
              (current.getFullYear() === annee && current.getMonth() < mois)
            ) {
              switch (d.frequence) {
                case '1 semaine': current.setDate(current.getDate() + 7); break;
                case '2 semaines': current.setDate(current.getDate() + 14); break;
                case '1 mois': current.setMonth(current.getMonth() + 1); break;
                case '3 mois': current.setMonth(current.getMonth() + 3); break;
                case '6 mois': current.setMonth(current.getMonth() + 6); break;
                case '1 an': current.setFullYear(current.getFullYear() + 1); break;
                default: current.setMonth(current.getMonth() + 1);
              }
            }

            // Si l'occurrence tombe ce mois-ci et la dépense est active
            if (
              current.getMonth() === mois &&
              current.getFullYear() === annee &&
              (!dateFin || current <= dateFin)
            ) {
              depensesDuMois.push({ ...d, occurrence_date: new Date(current) });
            }
            // NE PAS ALLER PLUS LOIN : on ne génère qu'une occurrence pour le mois courant
          }
        });
        setDepensesMois(depensesDuMois);
        setTotalDepensesMois(depensesDuMois.reduce((sum, d) => sum + Number(d.montant), 0));
      } catch (e) {
        setDepensesMois([]);
        setTotalDepensesMois(0);
      }
    };

    useEffect(() => {
      fetchDepensesMois();
    }, []);

    // Récupérer les dépenses Amazon du mois
    useEffect(() => {
      const fetchAmazonDepensesMois = async () => {
        try {
          const res = await api.get('/api/amazon-depenses');
          let depenses = Array.isArray(res.data) ? res.data : [];
          setAmazonDepensesMois(depenses);
          setTotalAmazonDepensesMois(depenses.reduce((sum, d) => sum + Number(d.montant), 0));
        } catch (e) {
          setAmazonDepensesMois([]);
          setTotalAmazonDepensesMois(0);
        }
      };
      fetchAmazonDepensesMois();
    }, []);

    const handleAchatsUpdate = async () => {
      await fetchData();
      setAchatsUpdateTrigger(t => t + 1);
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Cards de solde, achats, stock, crédit TVA, etc. EN PREMIER */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr) 8px 1fr' },
                gap: 2,
                mb: 4,
                alignItems: 'stretch',
              }}
            >
              <Box sx={{ gridColumn: { md: 1 } }}>
                <CardMetric
                  title="Solde Qonto"
                  value={qontoBalance?.amount || 0}
                  valueColor="text-primary"
                  icon={<Banknote className="w-4 h-4" />}
                  caption={qontoBalance?.iban ? `IBAN: ${qontoBalance.iban}` : 'Solde bancaire Qonto'}
                  detailDialog={{
                    title: 'Transactions récentes Qonto',
                    content: qontoDialogContent,
                    actionTooltip: 'Voir les transactions',
                    onOpen: handleOpenQontoDialog,
                    isOpen: isQontoDialogOpen,
                    setIsOpen: setIsQontoDialogOpen
                  }}
                />
              </Box>
              <Box sx={{ gridColumn: { md: 2 } }}>
                <CardMetric
                  title="Solde Amazon"
                  value={amazonBalance?.amount || 0}
                  valueColor="text-yellow-600"
                  icon={<Store className="w-4 h-4" />}
                  caption="Disponible sur Seller Central"
                  detailDialog={{
                    title: 'Détail des soldes Amazon par pays',
                    content: amazonDialogContent,
                    actionTooltip: 'Voir détail par pays'
                  }}
                />
              </Box>
              <Box sx={{ gridColumn: { md: 3 } }}>
                <CardMetric
                  title="Achats en cours"
                  value={coutTotalAchatsEnCours}
                  valueColor="text-blue-600"
                  icon={<ShoppingCart className="w-4 h-4" />}
                  caption={`${achatsEnCours?.length || 0} produit${achatsEnCours?.length > 1 ? 's' : ''} en attente`}
                  detailDialog={{
                    title: 'Détail des achats en cours',
                    content: achatsDialogContent,
                    actionTooltip: 'Voir détail'
                  }}
                />
              </Box>
              <Box sx={{ gridColumn: { md: 4 } }}>
                <CardMetric
                  title="Stock Amazon"
                  value={stockAmazon}
                  valueColor="text-green-600"
                  icon={<Package className="w-4 h-4" />}
                  caption={`${achatsStockAmazon?.length || 0} produit${achatsStockAmazon?.length > 1 ? 's' : ''} en vente`}
                  detailDialog={{
                    title: 'Détail du stock Amazon',
                    content: stockDialogContent,
                    actionTooltip: 'Voir détail'
                  }}
                />
              </Box>
              <Box sx={{ gridColumn: { md: 5 } }}>
                <CardMetric
                  title="Crédit TVA"
                  value={tvaCredit}
                  valueColor={tvaCredit >= 0 ? 'text-green-600' : 'text-red-600'}
                  icon={<Percent className="w-4 h-4" />}
                  caption="Manuel"
                  editable={true}
                  storageKey="card-tva"
                  onValueChange={setTvaCredit}
                />
              </Box>
              {/* Séparateur vertical */}
              <Box sx={{ gridColumn: { md: 6 }, borderLeft: '1.5px solid #e0e0e0', height: '100%', alignSelf: 'stretch', mx: 0, p: 0 }} />
              <CardMetric
                title="Fond de roulement"
                value={
                  (qontoBalance?.amount || 0) +
                  (amazonBalance?.amount || 0) +
                  (coutTotalAchatsEnCours || 0) +
                  (stockAmazon || 0) +
                  (tvaCredit || 0)
                }
                valueColor="text-secondary"
                icon={<Wallet className="w-4 h-4" />}
                caption="Cumul des soldes"
              />
            </Box>

            {/* Grille principale : Synthèse des ventes + Dernières commandes */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3, mb: 4, minHeight: 420, alignItems: 'stretch' }}>
              {/* Synthèse des ventes */}
              <Card sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, mt: 0 }}>Synthèse des ventes</Typography>
                  <Tabs
                    value={selectedPeriod}
                    onChange={(_, v) => v && setSelectedPeriod(v)}
                    textColor="primary"
                    indicatorColor="primary"
                    sx={{ minHeight: 36 }}
                  >
                    {periodOptions.map(opt => (
                      <Tab key={opt.value} value={opt.value} label={opt.label} sx={{ minHeight: 36, fontWeight: 600 }} />
                    ))}
                  </Tabs>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <MetricBadge label="CA" value={periodMetrics.ventes} />
                  <MetricBadge label="Commandes" value={periodMetrics.commandes} isCount />
                  <MetricBadge label="Profit" value={periodMetrics.profit} color={periodMetrics.profit >= 0 ? 'success' : 'error'} />
                  <MetricBadge label="ROI" value={periodMetrics.moyenneROI} isPercent />
                  <MetricBadge label="Marge" value={periodMetrics.moyenneMarge} isPercent />
                  <MetricBadge label="Refunds" value={periodMetrics.refundCount} color="error" isCount />
                </Box>
                <Box sx={{ flex: 1, minHeight: 180, mb: 2 }}>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" />
                      <YAxis tickFormatter={v => Number(v).toFixed(2) + ' €'} />
                      <RechartsTooltip formatter={(value, name) => [Number(value).toFixed(2) + ' €', name]} />
                      <Bar dataKey="ventes" fill="#1976d2" name="CA" />
                      <Bar dataKey="profit" fill="#43a047" name="Profit" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Top produits</Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {topProductsPeriod.map(prod => (
                      <Card key={prod.id} sx={{ p: 1, minWidth: 120, flex: 1 }}>
                        <Typography variant="body2" noWrap>{prod.titre}</Typography>
                        <Typography variant="caption" color="text.secondary">CA : {formatCurrency(prod.ventes)}</Typography><br />
                        <Typography variant="caption" color="text.secondary">Profit : {formatCurrency(prod.profit)}</Typography>
                      </Card>
                    ))}
                  </Box>
                </Box>
              </Card>
              {/* Dernières commandes enrichie */}
              <Card sx={{ p: 2, maxHeight: 510, height: '100%', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, mt: 0 }}>Dernières commandes</Typography>
                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                  {commandesFiltrees.slice(0, 8).map((cmd, idx) => {
                    const produit = getProduit(cmd.Produit);
                    const imgUrl = produit?.ASIN ? `https://images-na.ssl-images-amazon.com/images/P/${produit.ASIN}.01._SCLZZZZZZZ_.jpg` : null;
                    const isLast = idx === commandesFiltrees.slice(0, 8).length - 1;
                    return (
                      <Box key={cmd.id} sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: isLast ? 0 : 2,
                        p: 1,
                        borderRadius: 1,
                        '&:hover': { bgcolor: '#f5f5f5' },
                        borderBottom: isLast ? 'none' : '1px solid #eee',
                        background: '#fff',
                      }}>
                        {/* Image produit carrée, nette */}
                        <Box sx={{ width: 48, height: 48, mr: 2, borderRadius: 2, overflow: 'hidden', bgcolor: '#fff', border: '1px solid #eee', flexShrink: 0 }}>
                          {imgUrl ? (
                            <img src={imgUrl} alt={produit?.Titre} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                          ) : (
                            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: 22 }}>?</Box>
                          )}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap title={produit?.Titre}>{produit?.Titre || '-'}</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <span>{formatDate(cmd.DateCommande)}</span>
                            {cmd.Pays && <img src={countryFlags[cmd.Pays]} alt={cmd.Pays} style={{ width: 18, height: 12 }} />}
                            <Chip label={cmd.Statut} size="small" color={STATUS_COLORS[cmd.Statut] || 'default'} />
                            {cmd.Business === true && (
                              <Chip label="Business" color="info" size="small" sx={{ fontWeight: 700, ml: 1, background: '#e3f2fd', color: '#1976d2' }} />
                            )}
                          </Box>
                          {/* Ligne de métriques lisible, labels courts, tout sur une ligne */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {/* Ligne 1 : Prix, Coût, TVA, Frais (grille 4 colonnes, badges égaux) */}
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, width: '100%' }}>
                              <BadgeMetricCompact label="Prix" value={formatAmount(cmd.PrixDeVente)} />
                              <BadgeMetricCompact
                                label="Coût"
                                value={formatAmount(cmd.cout_achat_total)}
                                tooltip={(() => {
                                  const achat = getAchat(cmd.Achat);
                                  const prixAchat = achat?.prixAchat || achat?.["Prix d'achat"] || 0;
                                  const fraisPrep = achat?.frais_preparation || 0;
                                  const fraisLiv = achat?.frais_livraison || 0;
                                  const lignes = [];
                                  if (prixAchat > 0) lignes.push(<div key="pa">Prix d'achat : {formatAmount(prixAchat)}</div>);
                                  if (fraisPrep > 0) lignes.push(<div key="prep">Frais de préparation : {formatAmount(fraisPrep)}</div>);
                                  if (fraisLiv > 0) lignes.push(<div key="liv">Frais de livraison : {formatAmount(fraisLiv)}</div>);
                                  return lignes.length > 0 ? <Box>{lignes}</Box> : <span>-</span>;
                                })()}
                              />
                              <BadgeMetricCompact
                                label="TVA"
                                value={formatAmount(cmd.TVA)}
                                tooltip={(() => {
                                  const txTvaVente = cmd.TxTVA || null;
                                  const prixVente = cmd.PrixDeVente || 0;
                                  const prixAchat = cmd.prixAchatAchat || 0;
                                  const txTvaAchat = cmd.txTvaAchat || null;
                                  const tvaCollectee = (prixVente && txTvaVente) ? ((Number(prixVente) / (1 + Number(txTvaVente)/100)) * (Number(txTvaVente)/100)) : null;
                                  const tvaDeductible = (prixAchat > 0 && txTvaAchat) ? ((Number(prixAchat) / (1 + Number(txTvaAchat)/100)) * (Number(txTvaAchat)/100)) : null;
                                  return (
                                    <Box>
                                      <Typography variant="subtitle2" sx={{ mb: 1, color: '#000', fontWeight: 700 }}>TVA</Typography>
                                      <Box>
                                        <div>TVA collectée : {tvaCollectee !== null ? formatAmount(tvaCollectee) : '-'} {txTvaVente ? `- TVA : ${txTvaVente}%` : ''}</div>
                                        <div>TVA déductible : {tvaDeductible !== null ? formatAmount(tvaDeductible) : '-'} {txTvaAchat ? `- TVA : ${txTvaAchat}%` : ''}</div>
                                      </Box>
                                    </Box>
                                  );
                                })()}
                              />
                              <BadgeMetricCompact
                                label="Frais"
                                value={formatAmount((Number(cmd.fraisFBA) || 0) + (Number(cmd.fraisReferral) || 0) + (Number(cmd.feeVariable) || 0) + (Number(cmd.feeDigitalServices) || 0))}
                                tooltip={(() => {
                                  const frais = [];
                                  if (Number(cmd.fraisFBA) > 0) frais.push(<div key="fba">FBA : {formatAmount(cmd.fraisFBA)}</div>);
                                  if (Number(cmd.fraisReferral) > 0) frais.push(<div key="ref">Referral : {formatAmount(cmd.fraisReferral)}</div>);
                                  if (Number(cmd.feeVariable) > 0) frais.push(<div key="var">Variable closing : {formatAmount(cmd.feeVariable)}</div>);
                                  if (Number(cmd.feeDigitalServices) > 0) frais.push(<div key="dig">Services numériques : {formatAmount(cmd.feeDigitalServices)}</div>);
                                  return frais.length > 0 ? <Box>{frais}</Box> : <span>Aucun frais</span>;
                                })()}
                              />
                            </Box>
                            {/* Ligne 2 : Profit, ROI, Marge (grille 3 colonnes, alignée) */}
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, width: '100%', mt: 0.5 }}>
                              <BadgeMetricColored 
                                label="Profit" 
                                value={formatAmount(cmd.Profit)} 
                                colorObj={getColorForValue(cmd.Profit, thresholds.profit)} 
                              />
                              <BadgeMetricColored 
                                label="ROI" 
                                value={Number(cmd.ROI).toFixed(1) + '%'} 
                                colorObj={getColorForValue(cmd.ROI, thresholds.roi)} 
                              />
                              <BadgeMetricColored 
                                label="Marge" 
                                value={Number(cmd.Marge).toFixed(1) + '%'} 
                                colorObj={getColorForValue(cmd.Marge, thresholds.marge)} 
                              />
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Card>
            </Box>

            {/* Grille : Performance mensuelle (1/3) + Historique des repricing (2/3) */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 3, mb: 4 }}>
              {/* Carte des performances mensuelles */}
              <Card sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, mt: 0 }}>
                  Performance mensuelle
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Typography variant="body2" color="text.secondary">Ventes du mois</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1.08em' }}>{formatCurrency(monthlyMetrics.ventes)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Typography variant="body2" color="text.secondary">Profit total</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: monthlyMetrics.profit >= 0 ? '#166534' : '#991b1b', fontSize: '1.08em' }}>{formatCurrency(monthlyMetrics.profit)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Typography variant="body2" color="text.secondary">Dépenses du mois</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#dc2626', fontSize: '1.08em' }}>{formatCurrency(totalDepensesMois)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Typography variant="body2" color="text.secondary">Dépenses Amazon du mois</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#dc2626', fontSize: '1.08em' }}>{formatCurrency(totalAmazonDepensesMois)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Typography variant="body2" color="text.secondary">Refunds du mois</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#991b1b', fontSize: '1.08em' }}>{monthlyMetrics.refundCount} ({formatCurrency(monthlyMetrics.refundLoss)})</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Typography variant="body2" color="text.secondary">Profit réel</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: (monthlyMetrics.profit - totalDepensesMois - totalAmazonDepensesMois - monthlyMetrics.refundLoss) >= 0 ? '#166534' : '#991b1b', fontSize: '1.08em' }}>{formatCurrency(monthlyMetrics.profit - totalDepensesMois - totalAmazonDepensesMois - monthlyMetrics.refundLoss)}</Typography>
                  </Box>
                </Box>
              </Card>
              {/* Carte d'historique des repricing rules */}
              <RepricingHistoryCard />
            </Box>

            {/* Performance mensuelle, produits les plus rentables, etc. */}
            <Box sx={{ mt: 4, mb: 4 }}>
              {/* Produits les plus rentables du mois ET Dépenses Amazon du mois ET Dépenses du mois côte à côte */}
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 4 }}>
                <Paper sx={{ flex: 1, minWidth: 320, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0', p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, mt: 0 }}>
                    Produits les plus rentables du mois
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Produit</TableCell>
                            <TableCell align="right">Ventes</TableCell>
                            <TableCell align="right">Profit</TableCell>
                            <TableCell align="right">ROI</TableCell>
                            <TableCell align="right">Marge</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {topProducts.map((product, index) => (
                            <TableRow
                              key={index}
                              sx={{ '&:hover': { bgcolor: '#f8fafc' }, cursor: 'pointer' }}
                              onClick={() => handleOpenProductDetail(product.id)}
                            >
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {product.titre}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2">
                                  {formatCurrency(product.ventes)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  variant="body2"
                                  sx={{ color: product.profit >= 0 ? '#166534' : '#991b1b', fontWeight: 600 }}
                                >
                                  {formatCurrency(product.profit)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  variant="body2"
                                  sx={{ color: product.roi >= 50 ? '#166534' : product.roi >= 30 ? '#ca8a04' : '#991b1b', fontWeight: 600 }}
                                >
                                  {product.roi.toFixed(1)}%
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  variant="body2"
                                  sx={{ color: product.marge >= 30 ? '#166534' : product.marge >= 15 ? '#ca8a04' : '#991b1b', fontWeight: 600 }}
                                >
                                  {product.marge.toFixed(1)}%
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Paper>
                {/* Nouvelle card Dépenses Amazon du mois */}
                <Paper sx={{ minWidth: 320, maxWidth: 420, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0', p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, mt: 0 }}>
                    Dépenses Amazon du mois
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    {amazonDepensesMois.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">Aucune dépense Amazon ce mois-ci.</Typography>
                    ) : (
                      amazonDepensesMois.map((d, idx) => (
                        <Box key={d.id || d.type + '-' + idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body2">{d.description || d.type}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(d.montant)}</Typography>
                        </Box>
                      ))
                    )}
                  </Box>
                  <Divider sx={{ mb: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle2">Total</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#dc2626' }}>{formatCurrency(totalAmazonDepensesMois)}</Typography>
                  </Box>
                </Paper>
                <Paper sx={{ minWidth: 320, maxWidth: 420, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0', p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, mt: 0 }}>
                    Dépenses du mois
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    {depensesMois.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">Aucune dépense ce mois-ci.</Typography>
                    ) : (
                      depensesMois.map((d, idx) => (
                        <Box key={d.id + '-' + (d.occurrence_date ? d.occurrence_date.toISOString() : '')} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body2">{d.description}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(d.montant)}</Typography>
                        </Box>
                      ))
                    )}
                  </Box>
                  <Divider sx={{ mb: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle2">Total</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#dc2626' }}>{formatCurrency(totalDepensesMois)}</Typography>
                  </Box>
                </Paper>
              </Box>
            </Box>

            {/* Achats en attente, expédition, etc. */}
            <div style={{ display: 'flex', gap: 8, width: '100%' }}>
              <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                <AchatsEnAttenteReception
                  produits={produits}
                  fournisseurs={fournisseurs}
                  statuts={statuts}
                  onUpdate={handleAchatsUpdate}
                  achatsUpdateTrigger={achatsUpdateTrigger}
                  getStatutLabel={getStatutLabel}
                  getProduitLabel={getProduitLabel}
                  getFournisseurLabel={getFournisseurLabel}
                  formatDate={formatDate}
                  formatCurrency={formatCurrency}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                <AchatsEnAttenteExpedition
                  produits={produits}
                  fournisseurs={fournisseurs}
                  statuts={statuts}
                  onUpdate={handleAchatsUpdate}
                  achatsUpdateTrigger={achatsUpdateTrigger}
                  getStatutLabel={getStatutLabel}
                  getProduitLabel={getProduitLabel}
                  getFournisseurLabel={getFournisseurLabel}
                  formatDate={formatDate}
                  formatCurrency={formatCurrency}
                />
              </Box>
            </div>

            {/* Fichiers analysés */}
            <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
              Fichiers analysés
            </Typography>
            {loading ? (
              <TableSkeleton />
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Nom du fichier</TableCell>
                      <TableCell>Date d'analyse</TableCell>
                      <TableCell>Lignes</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analysedFiles.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          Aucun fichier analysé
                        </TableCell>
                      </TableRow>
                    ) : (
                      analysedFiles.map((file) => (
                        <TableRow
                          key={file.id}
                          hover
                          onClick={() => handleFileClick(file)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {file.name}
                              <Badge
                                color="error"
                                variant="dot"
                                invisible={!file.isNew}
                                sx={{ '& .MuiBadge-badge': { width: 12, height: 12, borderRadius: '50%' } }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell>{file.analysisDate}</TableCell>
                          <TableCell>{file.lignes || 0}</TableCell>
                          <TableCell>
                            <Tooltip title="Consulter">
                              <IconButton
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleFileClick(file);
                                }}
                                color="primary"
                                size="small"
                                sx={{ mr: 1 }}
                              >
                                <Search size={20} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Supprimer">
                              <IconButton
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleDeleteClick(file);
                                }}
                                color="error"
                                size="small"
                              >
                                <Trash2 size={20} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
        </Container>
    );
};

export default Dashboard; 